import React, { useEffect, useState } from 'react';
import { round3 } from '../../utils/money';
import { useNavigate } from 'react-router-dom';
import Swal from '../../theme/swal';
import {
  IcFileText,
  IcBanknote,
  IcTruck,
  IcReceipt,
  IcArrowLeft,
  IcClose,
  IcClipboardList,
} from '../../theme/icons';
import supplierService from '../../services/supplierService';
import chargeService from '../../services/chargeService';
import paymentService from '../../services/paymentService';
import factureService from '../../services/factureService';

/**
 * Mobile version of the desktop Dashboard "Achat et paiement" popup.
 * Same 3 steps (menu → type → form) and the same submit logic, re-created here
 * so the mobile bottom-bar "Paiement" button behaves exactly like the web view.
 * Kept entirely inside mobile/ — the desktop Dashboard is untouched.
 */
// `initialStep` (owner Studio 2026-08-14) : la barre du bas ouvre le sheet
// Tuiles = même identité que le popup « Achat et paiement » du PC (Dashboard) :
// gray-100, bordure encre 1.5 px, icône + libellé encre (Ahmed 2026-10-04,
// « même design, même couleur » — fini les anciennes teintes bleu/vert/amber).
// directement sur « Type d'achat » ('achat') ou « Type de paiement » ('type') —
// le menu combiné ('menu') reste le défaut.
export default function MobilePaiementSheet({ open, onClose, initialStep = 'menu' }) {
  const navigate = useNavigate();
  const [payStep, setPayStep] = useState(initialStep); // 'menu' | 'achat' | 'type' | 'form'
  const [payType, setPayType] = useState(null);   // 'supplier' | 'charge'
  const [payTargets, setPayTargets] = useState([]);
  const [payTargetsLoading, setPayTargetsLoading] = useState(false);
  const [payTargetId, setPayTargetId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Reset to the opening step every time the sheet is opened.
  useEffect(() => {
    if (open) {
      setPayStep(initialStep);
      setPayType(null);
      setPayTargets([]);
      setPayTargetId('');
      setPayAmount('');
      setPayNote('');
    }
  }, [open, initialStep]);

  const close = () => { if (!paySubmitting) onClose(); };

  const goFacture = () => {
    onClose();
    navigate('/facturation/add');
  };

  const goCommande = () => {
    onClose();
    navigate('/commandes/a-commander');
  };

  const choosePayType = async (type) => {
    setPayType(type);
    setPayTargetId('');
    setPayStep('form');
    setPayTargetsLoading(true);
    try {
      const list = type === 'supplier'
        ? await supplierService.getAllSuppliers()
        : await chargeService.getAll();
      setPayTargets(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading payment targets:', err);
      setPayTargets([]);
      Swal.fire('Erreur', 'Impossible de charger la liste.', 'error');
    } finally {
      setPayTargetsLoading(false);
    }
  };

  const submitPayment = async () => {
    const amount = round3(parseFloat(payAmount));
    if (!payTargetId) {
      Swal.fire('Champ requis', `Veuillez sélectionner ${payType === 'supplier' ? 'un fournisseur' : 'une charge'}.`, 'warning');
      return;
    }
    if (!amount || amount <= 0) {
      Swal.fire('Montant invalide', 'Veuillez saisir un montant payé supérieur à 0.', 'warning');
      return;
    }
    setPaySubmitting(true);
    try {
      // For a SUPPLIER payment, apply the amount to the supplier's unpaid factures
      // (oldest first) so the supplier's balance actually goes down — same logic as
      // the desktop popup / an "acompte" on the supplier detail page.
      if (payType === 'supplier') {
        let factures = [];
        try {
          factures = await factureService.getAllFactures({ supplier_id: payTargetId, unpaid: true });
        } catch (e) {
          factures = [];
        }
        const unpaid = (factures || [])
          .filter((f) => (Number(f.amount_due) || 0) > 0)
          .sort((a, b) => new Date(a.date_added || 0) - new Date(b.date_added || 0));
        const totalDue = unpaid.reduce((s, f) => s + (Number(f.amount_due) || 0), 0);
        const EPS = 1e-6;
        // Pay the whole balance exactly when the amount covers it (avoid float dust).
        const payAll = amount >= totalDue - 5e-4;
        let remaining = payAll ? totalDue : amount;
        for (const f of unpaid) {
          if (remaining <= EPS) break;
          const due = Number(f.amount_due) || 0;
          if (due <= 0) continue;
          const pay = remaining >= due - EPS ? due : remaining;
          if (pay <= 0) continue;
          await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
          remaining -= pay;
        }
      }
      const payload = {
        payment_type: payType,
        amount,
        note: payNote || '',
        ...(payType === 'supplier' ? { supplier: payTargetId } : { charge: payTargetId }),
      };
      await paymentService.create(payload);
      onClose();
      const targetName = payTargets.find((t) => String(t.id) === String(payTargetId))?.name || '';
      Swal.fire({
        icon: 'success',
        title: 'Paiement enregistré',
        text: `${amount.toFixed(3)} DT — ${payType === 'supplier' ? 'Fournisseur' : 'Charge'}${targetName ? ` : ${targetName}` : ''}.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error creating payment:', err);
      Swal.fire('Erreur', "Impossible d'enregistrer le paiement.", 'error');
    } finally {
      setPaySubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0 bg-gray-900 rounded-t-2xl">
          <div className="flex items-center gap-2">
            {payStep !== initialStep && (
              <button
                onClick={() => setPayStep(payStep === 'form' ? 'type' : 'menu')}
                className="text-gray-400 hover:text-white p-1 -ml-1"
                title="Retour"
              >
                <IcArrowLeft />
              </button>
            )}
            <h2 className="modal-title flex items-center gap-2">
              {payStep === 'menu' ? 'Achat et paiement'
                : payStep === 'achat' ? "Type d'achat"
                : payStep === 'type' ? 'Type de paiement'
                : `Paiement — ${payType === 'supplier' ? 'Fournisseur' : 'Charge'}`}
            </h2>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-white p-1" title="Fermer">
            <IcClose />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          {payStep === 'menu' && (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setPayStep('achat')}
                className="flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcFileText className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Achat</span>
                <span className="text-xs text-gray-500 text-center">Facture ou commande</span>
              </button>
              <button
                onClick={() => setPayStep('type')}
                className="flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcBanknote className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Paiement</span>
                <span className="text-xs text-gray-500 text-center">Fournisseur ou charge</span>
              </button>
            </div>
          )}

          {payStep === 'achat' && (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={goFacture}
                className="flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcFileText className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Nouvelle<br />facture</span>
              </button>
              <button
                onClick={goCommande}
                className="flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcClipboardList className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Nouvelle<br />commande</span>
              </button>
            </div>
          )}

          {/* Cartes Fournisseur / Charge : même hauteur que la carte « Achat » du menu
              (icône + titre + sous-titre = 7.75rem / 8.75rem en sm) — owner 2026-09-10. */}
          {payStep === 'type' && (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => choosePayType('supplier')}
                className="min-h-[7.75rem] sm:min-h-[8.75rem] justify-center flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcTruck className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Fournisseur</span>
              </button>
              <button
                onClick={() => choosePayType('charge')}
                className="min-h-[7.75rem] sm:min-h-[8.75rem] justify-center flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
              >
                <IcReceipt className="text-3xl text-gray-900" />
                <span className="font-semibold text-gray-900 text-sm text-center">Charge</span>
              </button>
            </div>
          )}

          {payStep === 'form' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {payType === 'supplier' ? 'Fournisseur' : 'Charge'}
                </label>
                {payTargetsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /> Chargement…
                  </div>
                ) : (
                  <select
                    value={payTargetId}
                    onChange={(e) => setPayTargetId(e.target.value)}
                    className="inp w-full rounded-xl px-3 py-2 text-base"
                  >
                    <option value="">— Sélectionner {payType === 'supplier' ? 'un fournisseur' : 'une charge'} —</option>
                    {payTargets.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}
                {!payTargetsLoading && payTargets.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Aucun élément disponible.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant payé (DT)</label>
                <input
                  type="number" min="0" step="0.001" inputMode="decimal"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0.000"
                  className="inp w-full rounded-xl px-3 py-2 text-lg font-semibold tabular-nums"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                <input
                  type="text"
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="Référence, motif…"
                  className="inp w-full rounded-xl px-3 py-2 text-base"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer (form only) */}
        {payStep === 'form' && (
          <div className="flex gap-3 p-5 border-t border-gray-100 shrink-0">
            <button
              onClick={() => { setPayStep('type'); }}
              disabled={paySubmitting}
              className="btn-g flex-1 py-2.5 rounded-xl"
            >
              Retour
            </button>
            <button
              onClick={submitPayment}
              disabled={paySubmitting}
              className="btn-p flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
            >
              {paySubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              Enregistrer le paiement
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
